import { useAppDispatch, useAppSelector } from 'app/state'
import { createHash } from 'crypto'
import useAccountSpatialExtensionInfo from 'features/account/state/useAccountSpatialExtensionInfo'
import { ProviderType } from 'features/connections'
import { getSubscriptionBySlug } from 'features/data-observatory/state/dataObservatorySlice'
import { createExportingJobRequest } from 'features/jobs/state/jobsExportingSlice'
import { JobType, ProcessingType } from 'features/jobs/types'
import { useCurrentUser } from 'features/users/hooks/userQueryHooks'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import settings from 'settings'

function useExportHooks(table) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const subscription = useAppSelector((state) => getSubscriptionBySlug(state, table.slug || ''))
  const { dataProject, dataset } = useAccountSpatialExtensionInfo()
  const user = useCurrentUser()

  const [uri, setUri] = useState<string>()

  const fqn = `${dataProject}.${dataset}.${table.id}`
  const geoFqn =
    subscription?.associated_geography_table && `${dataProject}.${dataset}.${subscription?.associated_geography_table}`

  const generateUri = useCallback(() => {
    const accountId = user?.user_metadata.account_id
    const stringToHash = `${accountId}_${user?.user_id}_${Date.now()}`
    const hash = createHash('md5').update(stringToHash).digest('hex')
    return `${settings.exportBucket}/${accountId}/${table.slug}/${hash}/${table.slug}_*.csv`
  }, [user?.user_metadata.account_id, user?.user_id, table.slug])

  const handleExport = useCallback(
    (query) => {
      const metadata = {
        name: table.name,
        fqn,
        connection: ProviderType.CARTO_DW,
        type: JobType.Exporting,
        processingType: ProcessingType.ExportTable,
        object: 'subscription',
        uri,
        subscriptionPath: location.pathname,
        subscription: subscription,
        table: table
      }

      if (query) {
        dispatch(
          createExportingJobRequest({
            connection: ProviderType.CARTO_DW,
            processingJobPayload: { query, metadata, tableId: table.fqn }
          })
        )
      }
    },
    [dispatch, fqn, location.pathname, subscription, table, uri]
  )

  useEffect(() => {
    const uri = generateUri()
    setUri(uri)
  }, [generateUri])

  return {
    uri,
    fqn,
    geoFqn,
    regenerateUri: generateUri,
    handleExport
  }
}

export default useExportHooks
